import { Alert, Badge, Button, Group, Paper, SimpleGrid, Stack, Text, Title } from "@mantine/core";
import { IconRefresh } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/auth/useAuth";
import { env } from "@/config/env";
import { LoadingState } from "@/components/states";
import { money, timeLabel } from "./metrics";
import { fetchPlatformSnapshot } from "./platform";
import type { PlatformSnapshot } from "./platform";
import styles from "./dashboard.module.css";

export function PlatformBalanceView({
  data,
  loading,
  refreshing,
  error,
  refresh,
  zone,
  now,
}: {
  data?: PlatformSnapshot;
  loading: boolean;
  refreshing: boolean;
  error: Error | null;
  refresh: () => void;
  zone: string;
  now: number;
}) {
  const stale = !!data && now - data.fetchedAt > 5 * 60_000;
  const partial = !!data && data.balanceSnapshots !== data.wallets - data.pendingWallets;
  return (
    <Paper withBorder p="lg" radius="lg" className={styles.balances}>
      <Group justify="space-between" mb="md">
        <div>
          <Title order={3}>Platform balances</Title>
          <Text size="sm" c="dimmed">
            All accounts · independent of the chart window
          </Text>
        </div>
        <Group gap="xs">
          <Badge variant="light" color={stale || error ? "orange" : "gray"}>
            {error && data ? "Previous snapshot" : stale ? "Snapshot over 5 min old" : "Snapshot"}
          </Badge>
          <Button
            size="xs"
            variant="default"
            leftSection={<IconRefresh size={14} />}
            loading={refreshing}
            onClick={refresh}
          >
            Refresh totals
          </Button>
        </Group>
      </Group>
      {error && (
        <Alert color="orange" mb="md" title="Could not refresh platform totals">
          {error.message}
          {data ? " Showing the previous snapshot below." : " No totals are available."}
        </Alert>
      )}
      {partial && (
        <Alert color="orange" mb="md" title="Incomplete balance coverage">
          The number of balance snapshots differs from the number of provisioned wallets. This
          estimate may omit funds or include older projections; reconcile coverage before using it
          as a platform total.
        </Alert>
      )}
      {loading && !data ? (
        <LoadingState label="Calculating platform totals…" />
      ) : data ? (
        <>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xl">
            <Stack gap={3}>
              <Text size="sm" c="dimmed">
                Tracked customer assets · estimate
              </Text>
              <Text className={styles.balanceValue} data-testid="platform-total">
                {money(data.main + data.reserve + data.earn + data.pendingEarn)} <span>USDC</span>
              </Text>
              <Text size="xs" c="dimmed">
                Wallet USDC + Earn holdings + Earn queued to Main
              </Text>
            </Stack>
            <SimpleGrid cols={2} spacing="sm">
              {[
                ["Main", data.main],
                ["Reserve", data.reserve],
                ["Earn holdings", data.earn],
                ["Earn queued to Main", data.pendingEarn],
              ].map(([label, value]) => (
                <div key={String(label)}>
                  <Text size="xs" c="dimmed">
                    {String(label)}
                  </Text>
                  <Text fw={600}>
                    {money(value as bigint)}{" "}
                    <Text span size="xs" c="dimmed">
                      USDC
                    </Text>
                  </Text>
                </div>
              ))}
            </SimpleGrid>
          </SimpleGrid>
          <SimpleGrid cols={{ base: 2, sm: 4 }} mt="lg" pt="md" className={styles.balanceDetails}>
            {[
              ["Spendable on Main", `${money(data.spendable)} USDC`],
              ["Held on Main", `${money(data.held)} USDC`],
              ["Accounts", data.users.toLocaleString()],
              [
                "Provisioned wallet snapshots",
                `${data.balanceSnapshots.toLocaleString()} / ${(data.wallets - data.pendingWallets).toLocaleString()}`,
              ],
            ].map(([label, value]) => (
              <div key={label}>
                <Text size="xs" c="dimmed">
                  {label}
                </Text>
                <Text fw={600} size="sm">
                  {value}
                </Text>
              </div>
            ))}
          </SimpleGrid>
          <Text size="xs" c="dimmed" mt="md">
            Fetched {timeLabel(data.fetchedAt, zone)} · {data.earnPositions} Earn positions.
            Spendable and holds describe Main availability; they are not added to assets.
          </Text>
          <Text size="xs" c="dimmed" mt={4}>
            {data.wallets.toLocaleString()} wallets in total ·{" "}
            {data.pendingWallets.toLocaleString()} awaiting addresses, excluded from expected
            balance coverage.
          </Text>
          <Text size="xs" c="dimmed" mt={4}>
            Balances reflect separate backend snapshots and can lag transfers. Excludes Rain
            collateral, bridge funds in transit, and Earn exits to other receivers or fast queues.
            This is not a reconciled custody total.
          </Text>
        </>
      ) : null}
    </Paper>
  );
}

export function PlatformBalances({
  zone,
  now,
  visible,
}: {
  zone: string;
  now: number;
  visible: boolean;
}) {
  const { user } = useAuth();
  const snapshot = useQuery({
    // Keep aggregates isolated by environment AND operator; do not persist them to disk.
    queryKey: ["platform-balances", env.firebase.projectId, user?.uid],
    queryFn: fetchPlatformSnapshot,
    enabled: visible && !!user,
    staleTime: 60_000,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  return (
    <PlatformBalanceView
      data={snapshot.data}
      loading={snapshot.isPending}
      refreshing={snapshot.isFetching}
      error={snapshot.error}
      refresh={() => void snapshot.refetch()}
      zone={zone}
      now={now}
    />
  );
}
